import React, { useEffect, useRef, useState } from 'react';

import { UserStatusFilter } from '../../../api/usersApi';
// Reuso deliberado do CSS dos filtros do Contac (at-61). As classes `filtro-modal-*`,
// `filtro-section*`, `select-wrapper` e `filtro-btn-*` são genéricas e já servem 4 modais
// (`ModalFiltroPlaca`, os 3 `ModalReports*` e o `ModalPesquisaPorPlaca`) pelo mesmo import —
// recopiar o bloco é o erro que a at-44 registrou com o `.help-circle-btn`.
import '../contac/filtro/ModalFiltro.css';
import './ModalFiltroUsuarios.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Os 16 valores do enum `UserType`, com o rótulo que o usuário lê. O valor enviado ao backend é o
 * **enum**; o rótulo espelha o `UserTypeMap` (`src/common/enumMappings.ts`), que é o formato em que
 * a listagem devolve o `userType`. Tipo novo no backend precisa entrar aqui.
 */
const USER_TYPE_OPTIONS: { value: string; label: string }[] = [
  // Ordem alfabética pelo RÓTULO (pedido do Victor, 25/09/2026).
  { value: 'ADMINISTRACAO', label: 'Administração' },
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'CHEFE_DE_OFICINA', label: 'Chefe de Oficina' },
  { value: 'COLABORADOR', label: 'Colaborador' },
  { value: 'COMISSIONAMENTO', label: 'Comissionamento' },
  { value: 'COMPRA', label: 'Compra' },
  { value: 'CONSULTOR', label: 'Consultor' },
  { value: 'CONTAC', label: 'Contac' },
  { value: 'ESTOQUE', label: 'Estoque' },
  { value: 'EVENTOS', label: 'Eventos' },
  { value: 'GESTAO_COMPRAS', label: 'Gestão de Compras' },
  { value: 'INSPETOR', label: 'Inspetor' },
  { value: 'TELAO', label: 'Telão' },
  { value: 'ORCAMENTO', label: 'Orçamento' },
  { value: 'RASTREADOR', label: 'Rastreador' },
  { value: 'RECEPCAO', label: 'Recepção' },
  { value: 'REGIONAL', label: 'Regional' },
  { value: 'RELACIONAMENTO', label: 'Relacionamento' },
  { value: 'TI', label: 'TI' },
];

const STATUS_OPTIONS: { value: UserStatusFilter; label: string }[] = [
  { value: 'ATIVOS', label: 'Somente ativos' },
  { value: 'INATIVOS', label: 'Somente inativos' },
  { value: 'TODOS', label: 'Ativos e inativos' },
];

export interface FiltrosUsuarios {
  status: UserStatusFilter;
  /** Enum do tipo, ou `''` para todos. */
  userType: string;
  /** YYYY-MM-DD, ou `''`. */
  createdFrom: string;
  createdTo: string;
}

export const FILTROS_USUARIOS_PADRAO: FiltrosUsuarios = {
  status: 'ATIVOS',
  userType: '',
  createdFrom: '',
  createdTo: '',
};

interface ModalFiltroUsuariosProps {
  onClose: () => void;
  /** Filtros em vigor — o modal abre sempre refletindo o que está aplicado na tela. */
  filtrosAtuais: FiltrosUsuarios;
  onApplyFilters: (filtros: FiltrosUsuarios) => void;
  /** Só gestores escolhem status (mesma trava do backend). */
  podeFiltrarStatus?: boolean;
}

/**
 * Filtros da tela de Usuários (at-61), no molde visual do modal de filtros do Contac.
 *
 * O estado é **local ao modal** e só sai em "Aplicar Filtros": mexer no select não dispara
 * requisição, então quem quer trocar status **e** tipo faz uma busca, não duas. É também o que
 * permite "Cancelar" desfazer de fato.
 *
 * ⚠️ **Uma diferença deliberada em relação ao modal do Contac**: lá o componente fica sempre
 * montado, com `isOpen` na prop, `if (!isOpen) return null` e um `useEffect` que recopia as props
 * para o estado local a cada abertura. Aqui **quem monta e desmonta é a tela** — não existe prop
 * `isOpen`. Motivo medido na at-61: com o desenho do Contac, o rascunho descartado no "Cancelar"
 * **sobrevivia** e reaparecia na abertura seguinte (o efeito de reset disparava com o valor certo,
 * mas o React não re-renderizava). Montagem condicional torna o `useState` inicial a única fonte
 * do estado, e o caso deixa de existir por construção em vez de depender de um efeito.
 * (O mesmo desenho reproduzido isoladamente funciona, então isto **não** é diagnóstico de bug no
 * Contac — é a razão de não copiar o padrão aqui.)
 */
const ModalFiltroUsuarios: React.FC<ModalFiltroUsuariosProps> = ({
  onClose,
  filtrosAtuais,
  onApplyFilters,
  podeFiltrarStatus = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [filtros, setFiltros] = useState<FiltrosUsuarios>(filtrosAtuais);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const alterar = (campo: keyof FiltrosUsuarios, valor: string) => {
    setErro(null);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const handleApply = () => {
    // O backend também recusa (400), mas avisar aqui evita a ida e volta.
    if (filtros.createdFrom && filtros.createdTo && filtros.createdFrom > filtros.createdTo) {
      setErro('A data final não pode ser anterior à data inicial.');
      return;
    }

    onApplyFilters(filtros);
    onClose();
  };

  const handleLimpar = () => {
    setErro(null);
    setFiltros(FILTROS_USUARIOS_PADRAO);
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros de Usuários</h2>
              <p className="filtro-modal-subtitle">Os filtros se combinam entre si e com a busca por nome</p>
            </div>
            <button className="close-button" onClick={onClose} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
            {podeFiltrarStatus && (
              <div className="filtro-section">
                <h3 className="filtro-section-title">Situação do Cadastro</h3>
                <div className="filtro-field">
                  <label htmlFor="filtro-status">Status</label>
                  <div className="select-wrapper">
                    <select
                      id="filtro-status"
                      value={filtros.status}
                      onChange={(e) => alterar('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((opcao) => (
                        <option key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="filtro-section">
              <h3 className="filtro-section-title">Perfil de Acesso</h3>
              <div className="filtro-field">
                <label htmlFor="filtro-tipo">Tipo de usuário</label>
                <div className="select-wrapper">
                  <select
                    id="filtro-tipo"
                    value={filtros.userType}
                    onChange={(e) => alterar('userType', e.target.value)}
                  >
                    <option value="">Todos os tipos</option>
                    {USER_TYPE_OPTIONS.map((opcao) => (
                      <option key={opcao.value} value={opcao.value}>
                        {opcao.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="filtro-section">
              <div className="filtro-section-header">
                <h3 className="filtro-section-title">Data de Criação</h3>
                {(filtros.createdFrom || filtros.createdTo) && (
                  <button
                    type="button"
                    className="filtro-reset-btn"
                    onClick={() => {
                      setErro(null);
                      setFiltros((atual) => ({ ...atual, createdFrom: '', createdTo: '' }));
                    }}
                  >
                    Redefinir
                  </button>
                )}
              </div>
              <div className="filtro-grid-2">
                <div className="filtro-field">
                  <label htmlFor="filtro-criado-de">Criado de</label>
                  <input
                    id="filtro-criado-de"
                    type="date"
                    className="premium-text-input"
                    value={filtros.createdFrom}
                    onChange={(e) => alterar('createdFrom', e.target.value)}
                  />
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-criado-ate">Criado até</label>
                  <input
                    id="filtro-criado-ate"
                    type="date"
                    className="premium-text-input"
                    value={filtros.createdTo}
                    onChange={(e) => alterar('createdTo', e.target.value)}
                  />
                </div>
              </div>
              {erro && <p className="filtro-erro">{erro}</p>}
            </div>
          </div>

          <div className="filtro-modal-footer">
            <button type="button" className="filtro-btn-secondary filtro-btn-limpar" onClick={handleLimpar}>
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={handleApply}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalFiltroUsuarios;
